/**
 * Apollo Client configuration with authentication.
 *
 * Sets up Apollo Client with:
 * - HTTP link for queries and mutations (with auth header injection)
 * - WebSocket link for subscriptions (with auth via connection params)
 * - Split routing: subscriptions → WS, everything else → HTTP
 * - InMemoryCache with cursor-based pagination merge
 */

import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
  split,
} from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { getMainDefinition } from '@apollo/client/utilities'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { createClient } from 'graphql-ws'
import { msalInstance, apiRequest } from './msal-config'
import { isInTeams, getTeamsToken } from './teams-auth'

/**
 * GraphQL endpoint URL.
 *
 * Uses VITE_GRAPHQL_URL from environment or defaults to /graphql
 * which will be proxied by Vite in development.
 */
const GRAPHQL_URL = import.meta.env.VITE_GRAPHQL_URL || '/graphql'

/**
 * WebSocket URL for subscriptions.
 * Uses a separate /graphql-ws endpoint to avoid route conflicts with the
 * HTTP GraphQL handler (Strawberry registers a WS handler on every path,
 * and FastAPI resolves dependencies before the protocol check).
 */
const WS_URL = (() => {
  const wsPath = '/graphql-ws'
  if (GRAPHQL_URL.startsWith('http')) {
    const base = new URL(GRAPHQL_URL)
    base.protocol = base.protocol === 'https:' ? 'wss:' : 'ws:'
    base.pathname = wsPath
    return base.toString()
  }
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${protocol}//${window.location.host}${wsPath}`
})()

/**
 * Acquire an auth token using Teams SSO or MSAL.
 * Shared by both the HTTP auth link and WS connection params.
 */
export async function getAuthToken(): Promise<string> {
  try {
    const inTeams = await isInTeams()
    if (inTeams) {
      const teamsToken = await getTeamsToken()
      if (teamsToken) return teamsToken
    }

    const accounts = msalInstance.getAllAccounts()
    if (accounts.length === 0) return ''

    const response = await msalInstance.acquireTokenSilent({
      ...apiRequest,
      account: accounts[0],
    })
    return response.accessToken || ''
  } catch (error) {
    console.error('Error acquiring token:', error)
    return ''
  }
}

/**
 * HTTP link for GraphQL requests.
 */
const httpLink = new HttpLink({
  uri: GRAPHQL_URL,
  credentials: 'include',
})

/**
 * Authentication link that injects the Bearer token into HTTP requests.
 */
const authLink = setContext(async (_, { headers }) => {
  const token = await getAuthToken()
  return {
    headers: {
      ...headers,
      Authorization: token ? `Bearer ${token}` : '',
    },
  }
})

/**
 * WebSocket link for GraphQL subscriptions.
 * Auth token is sent via connectionParams on connect/reconnect.
 */
const wsLink = new GraphQLWsLink(
  createClient({
    url: WS_URL,
    connectionParams: async () => {
      const token = await getAuthToken()
      return { authToken: token }
    },
    shouldRetry: () => true,
    retryAttempts: Infinity,
  })
)

/**
 * Split link: subscriptions go over WebSocket, everything else over HTTP.
 */
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query)
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    )
  },
  wsLink,
  ApolloLink.from([authLink, httpLink]),
)

/**
 * Apollo Client instance.
 */
export const apolloClient: ApolloClient<NormalizedCacheObject> = new ApolloClient({
  link: splitLink,
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          tasks: {
            keyArgs: ['filter'],
            merge(existing, incoming, { args }) {
              const after = (args as Record<string, unknown> | null)?.after
              // Initial load or refetch (no cursor): full replace
              if (!existing || !after) return incoming
              // Subsequent pages: append items, deduplicate by id
              const existingIds = new Set(
                (existing.items ?? []).map((item: { __ref?: string }) => item.__ref),
              )
              const newItems = (incoming.items ?? []).filter(
                (item: { __ref?: string }) => !existingIds.has(item.__ref),
              )
              return {
                ...incoming,
                items: [...(existing.items ?? []), ...newItems],
              }
            },
          },
        },
      },
      Task: {
        keyFields: ['id'],
      },
    },
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
    },
    query: {
      fetchPolicy: 'cache-first',
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
})

export default apolloClient
